import { useRef } from 'react';
import { useApp } from '@context/AppContext';
import { PALETTE } from '@utils/colours';

export default function ColourPalette() {
  const { state, dispatch } = useApp();
  const activeColour = state.canvas.colour;
  const activeStyle = state.canvas.activeStyle;
  const pickerRef = useRef(null);

  // When a style is active, show only the style's palette.
  // Otherwise show the full 20-colour default palette.
  const displayedPalette = activeStyle && activeStyle.palette && activeStyle.palette.length > 0
    ? activeStyle.palette.map((hex) => ({ name: activeStyle.name, hex }))
    : PALETTE;

  const handleCustomColour = (e) => {
    dispatch({ type: 'SET_COLOUR', payload: e.target.value });
  };

  const handleClearStyle = () => {
    dispatch({ type: 'CLEAR_ACTIVE_STYLE' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Style cleared — full palette restored' } });
  };

  return (
    <div className="flex flex-col items-center gap-1 px-1">
      {/* Active style indicator */}
      {activeStyle && (
        <div
          className="mb-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono text-white uppercase tracking-wider truncate max-w-[52px] text-center"
          style={{ backgroundColor: activeStyle.colour }}
          title={`${activeStyle.name} preset active — palette restricted`}
        >
          {activeStyle.emoji}
        </div>
      )}

      {/* Swatch grid — 2 columns to fit in 60px toolbar */}
      <div className="grid grid-cols-2 gap-1">
        {displayedPalette.map(({ name, hex }, idx) => (
          <button
            key={`${hex}-${idx}`}
            onClick={() => dispatch({ type: 'SET_COLOUR', payload: hex })}
            title={name}
            className="w-5 h-5 rounded-full transition-transform hover:scale-125 relative"
            style={{ backgroundColor: hex }}
          >
            {activeColour === hex && (
              <span className="absolute inset-0 rounded-full ring-2 ring-white ring-offset-1 ring-offset-charcoal" />
            )}
          </button>
        ))}
      </div>

      {/* Custom colour picker (only when no style is active) */}
      {!activeStyle && (
        <>
          <button
            onClick={() => pickerRef.current?.click()}
            className="mt-1 w-8 h-8 rounded-full border-2 border-dashed border-slate-500 hover:border-amber transition-colors flex items-center justify-center overflow-hidden"
            title="Custom colour"
          >
            <div
              className="w-full h-full rounded-full"
              style={{
                background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
              }}
            />
          </button>
          <input
            ref={pickerRef}
            type="color"
            value={activeColour}
            onChange={handleCustomColour}
            className="sr-only"
          />
        </>
      )}

      {/* Clear style button (only when a style IS active) */}
      {activeStyle && (
        <button
          onClick={handleClearStyle}
          title="Clear style preset"
          className="mt-1 w-8 h-8 rounded-full border-2 border-dashed border-slate-500 hover:border-red-400 text-slate-400 hover:text-red-400 transition-colors flex items-center justify-center text-xs"
        >
          ✕
        </button>
      )}
    </div>
  );
}
